import type {
  Attachment,
  AttachmentType,
  Document,
  DocumentUploadTypesConfig,
  OrdinanceData,
  ResolutionData,
} from "@repo/types";
import { Formik } from "formik";
import * as yup from "yup";
import { Input } from "@repo/ui/components/Base/Input";
import { AutocompleteInput } from "@repo/ui/components/Base/AutocompleteInput";
import { TextArea } from "@repo/ui/components/Base/TextArea";
import { Button } from "@repo/ui/components/Base/Button";
import { TagSelection } from "../tags";
import { Label } from "@repo/ui/components/Base/Label";
import { FileUpload } from "@repo/ui/components/Base/FileUpload";
import {
  arrayify,
  capitalize,
  cn,
  downloadFile,
  fixInvalidURL,
  formatTZ,
  getURLHostname,
  openInNewTab,
} from "@repo/shared/utils";
import { useUserStore } from "@/stores/userStore";
import { useContext, useMemo, useRef, useState } from "react";
import { FormikCheckbox } from "../common/FormikCheckbox";
import { CheckboxGroupInput } from "@repo/ui/components/Base/CheckboxGroupInput";
import { useTrackedNewObjects } from "@/hooks/trackCreatedObjects";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import { DATETIME_FORMAT } from "@/lib/constants";
import { useTenantStore } from "@/stores/tenantStore";
import { FileDisplay } from "@repo/ui/components/Base/FileDisplay";
import { DocumentDefaultsContext } from "./DocumentDefaults";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { apiService } from "@/services/apiService";
import { addDays } from "date-fns";
import { TitleComposer } from "../agendaItems/TitleComposer";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import { RadioInput } from "@repo/ui/components/Base/RadioInput";
import type { OnlyRequire } from "@repo/lib/types";
import shortUUID from "short-uuid";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { mergeDefined } from "@repo/lib/mergeDefined";
import { FilePenLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { extractExtension } from "@repo/lib/extractExtension";
import { ConfirmDialog } from "@repo/ui/components/Base/ConfirmDialog";
import { getLegislationNumberFormatLabel } from "@repo/shared/legislationNumbering";
import { LegislationNumberInput } from "./legislationNumbering/LegislationNumberInput";
import { LegislationNumberReservation } from "./legislationNumbering/LegislationNumberReservation";
import { useLegislationNumberReservation } from "./legislationNumbering/useLegislationNumberReservation";
import type { LegislationNumberField } from "./legislationNumbering/types";
import { useGetDocumentUploadTypes } from "@/hooks/queryHooks/configHooks";
import { useMeetingList } from "@/hooks/queryHooks/meetingHooks";
import { useActiveTeamId, useActiveTeamIdOrOrg } from "@/hooks/useActiveTeam";
import { useOrdinanceList } from "@/hooks/queryHooks/documentHooks";
import { documentRepo, type DocumentDto } from "@/repositories/documentRepo";
import { attachmentRepo } from "@/repositories/attachmentRepo";

const fileUploadLimitURLs = {
  counties:
    "https://www.pa.gov/content/dam/copapwp-pagov/en/phmc/documents/archives/records-management/documents/2026-county-records-manual-final.pdf",
  municipalities:
    "https://www.pa.gov/content/dam/copapwp-pagov/en/phmc/documents/archives/records-management/documents/2019-Municipal-Records-Manual-rev-with-links.pdf",
  ada: "https://www.ada.gov/resources/2024-03-08-web-rule/",
};

const ordinanceStatusList: NonNullable<OrdinanceData["status"]>[] = [
  "proposed",
  "passed",
  "failed",
];
const resolutionStatusList: NonNullable<ResolutionData["status"]>[] = [
  "proposed",
  "passed",
  "failed",
];

const attachmentTypeLabels: Record<AttachmentType, string> = {
  editable: "Editable file (e.g., *.docx)",
  original: "Original file",
  signed: "Signed PDF file",
};
const attachmentTypeList = Object.keys(
  attachmentTypeLabels
) as AttachmentType[];

type AugmentedAttachment = OnlyRequire<
  Attachment,
  "attachmentType" | "attachmentId"
> & {
  $file:
    | File
    | { size: number; name: string; url: string; path: string }
    | null;
  $url: string;
};

type AugmentedDocument = Omit<DocumentDto, "attachments"> & {
  attachments: AugmentedAttachment[];
};

/** Copy / update attachments array so it always contains augmented attachments for the listed
 * types. */
const prepareAugmentedAttachments = (
  attachments: Attachment[]
): AugmentedAttachment[] => {
  const seen = new Set<AttachmentType>();

  const updated: AugmentedAttachment[] = attachments.map((attachment) => {
    seen.add(attachment.attachmentType);
    const { isUrl } = attachment;
    return mergeDefined(
      {
        $file: isUrl
          ? null
          : {
              size: attachment.sizeBytes ?? 0,
              name: attachment.name,
              url: attachment.url,
              path: attachment.path,
            },
        $url: isUrl ? attachment.url : "",
      },
      attachment
    );
  });

  for (const type of attachmentTypeList) {
    if (!seen.has(type)) {
      updated.push({
        attachmentType: type,
        attachmentId: shortUUID.generate(),
        $file: null,
        $url: "",
      });
    }
  }

  return updated;
};

/** Determine if an augmented attachment is capable of being the linked attachment. */
const isLinkable = (attachment: AugmentedAttachment) =>
  (attachment.attachmentType === "original" ||
    attachment.attachmentType === "signed") &&
  (attachment.isUrl ? attachment.$url !== "" : attachment.$file !== null);

/** Returns if there are any augmented attachments with actually linked files or urls. */
const hasRealAttachments = (attachments: AugmentedAttachment[]) => {
  return attachments.some(
    (attachment) => attachment.$file || attachment.$url || attachment.url
  );
};

const isSignablePdfAttachment = (attachment?: AugmentedAttachment | null) => {
  if (!attachment || attachment.isUrl || !attachment.$file) return false;

  const extension =
    attachment.extension ?? extractExtension(attachment.$file.name);

  return extension?.toLowerCase() === "pdf";
};

const getOriginalAttachmentSignature = (
  attachments?: AugmentedAttachment[] | Attachment[]
) => {
  const original = attachments?.find(
    (attachment) => attachment.attachmentType === "original"
  ) as AugmentedAttachment | undefined;

  if (!original || (!original.$file && !original.$url && !original.url)) {
    return "";
  }

  const file =
    original.$file instanceof File
      ? {
          lastModified: original.$file.lastModified,
          name: original.$file.name,
          size: original.$file.size,
        }
      : original.$file;

  return JSON.stringify({
    attachmentId: original.attachmentId,
    extension: original.extension,
    file,
    isUrl: original.isUrl,
    name: original.name,
    path: original.path,
    sizeBytes: original.sizeBytes,
    url: original.isUrl ? original.$url || original.url : original.url,
  });
};

export interface DocumentAttachmentGridHeaderProps {
  cellClassName?: string;
}
export const DocumentAttachmentGridHeader = ({
  cellClassName,
}: DocumentAttachmentGridHeaderProps) => {
  cellClassName = cn("text-left font-bold", cellClassName);

  return (
    <div className="grid grid-cols-subgrid col-span-full">
      <div className={cellClassName}>Link</div>
      <div className={cellClassName}>Version of file</div>
      <div className={cellClassName}>URL</div>
      <div className={cellClassName}>Drag and drop file, or enter URL</div>
      <div className={cellClassName} />
    </div>
  );
};

export interface DocumentAttachmentRowProps {
  disabled?: boolean;
  viewOnly?: boolean;
  downloadable?: boolean;
  confidential?: boolean;
  tenantId: string;
  documentId?: string;
  attachmentType: AttachmentType;
  attachment: AugmentedAttachment;
  hasSigningTemplate?: boolean;
  linkedAttachment?: string;
  uploadType?: DocumentUploadTypesConfig["uploadTypes"][0];
  onLinkSelect?: (attachmentType: AttachmentType) => void;
  onIsUrlChange?: (attachmentType: AttachmentType, useUrl: boolean) => void;
  onUrlChange?: (attachmentType: AttachmentType, url: string) => void;
  onFileChange?: (AttachmentType: AttachmentType, file: File | null) => void;
}
export const DocumentAttachmentRow = ({
  disabled,
  viewOnly,
  downloadable,
  confidential,
  tenantId,
  documentId,
  attachmentType,
  attachment,
  hasSigningTemplate,
  linkedAttachment,
  uploadType,
  onLinkSelect,
  onIsUrlChange,
  onUrlChange,
  onFileChange,
}: DocumentAttachmentRowProps) => {
  const navigate = useNavigate();
  const tenant = useTenantStore((s) => s.tenant);
  const canBeUrl =
    attachmentType === "editable" ||
    (!!uploadType && uploadType.storage !== "long");

  const uploadAccept =
    attachmentType !== "editable" && uploadType?.storage === "long"
      ? "compliant-docs"
      : "all";
  const showPrepareForSigningIcon =
    tenant?.featureFlags?.signatures &&
    attachmentType === "original" &&
    (hasSigningTemplate ||
      (!attachment.isUrl && attachment.extension?.toLowerCase() === "pdf"));
  const canOpenSignForm = showPrepareForSigningIcon && !!documentId;

  const handleDownload = useFutureCallback(async () => {
    if (!downloadable) return;

    if (attachment.isUrl) {
      const url = fixInvalidURL(attachment.$url);

      if (url) openInNewTab(url);
    } else {
      const urlOrBlob =
        !attachment.$file || attachment.$file instanceof File
          ? attachment.$file
          : confidential
            ? documentId
              ? (await apiService.v1.documentDownloadUrl(tenantId, documentId))
                  .data
              : null
            : attachment.$file.url;

      if (urlOrBlob) downloadFile(urlOrBlob, attachment.name);
    }
  });

  return (
    <div className="grid col-span-full grid-cols-subgrid items-center">
      <div>
        {attachmentType !== "editable" ? (
          <Tooltip
            label="Points link to this version"
            tooltipTriggerProps={{ className: "block" }}
          >
            <RadioInput
              disabled={
                disabled || viewOnly || (!attachment.$file && !attachment.$url)
              }
              containerStyle="mt-0"
              checked={
                !!linkedAttachment &&
                linkedAttachment === attachment?.attachmentId
              }
              onCheckedChange={() => onLinkSelect?.(attachmentType)}
            />
          </Tooltip>
        ) : null}
      </div>
      <div>{attachmentTypeLabels[attachmentType]}</div>
      <div>
        <Tooltip asChild label="Use a URL instead">
          <CheckboxInput
            disabled={!canBeUrl || !!attachment.$file || viewOnly || disabled}
            containerStyle="mt-0"
            checked={attachment?.isUrl ?? false}
            onCheckedChange={(checked) =>
              onIsUrlChange?.(attachmentType, !!checked)
            }
          />
        </Tooltip>
      </div>
      <div>
        {viewOnly ? (
          /* View only: display uploaded file / url */
          <FileDisplay
            disabled={disabled}
            name={
              !attachment.url && !(attachment.$file instanceof File)
                ? "No attachment"
                : attachment.isUrl && !confidential
                  ? getURLHostname(attachment.url ?? "")
                  : attachment.name ?? "<Unnamed>"
            }
            size={attachment.sizeBytes ?? 0}
            onDownload={
              downloadable && (attachment.$url || attachment.$file)
                ? handleDownload
                : undefined
            }
            onRemove={() => {
              onFileChange?.(attachmentType, null);
            }}
          />
        ) : attachment.isUrl ? (
          /** Edit URL */
          <Input
            id={attachmentType + "-url"}
            disabled={!canBeUrl || disabled}
            value={attachment.$url ?? ""}
            onChange={(evt) => {
              onUrlChange?.(attachmentType, evt.target.value);
            }}
          />
        ) : !attachment.$file ? (
          /** No file was dropped in yet */
          <FileUpload
            small
            disabled={disabled}
            accept={uploadAccept}
            handleFileUpload={(file) => {
              onFileChange?.(attachmentType, file);
            }}
          />
        ) : (
          /** If $file is a File, file was dropped but not saved and uploaded.
           *  Otherwise, user is editing an already saved attachment. */
          <FileDisplay
            disabled={disabled}
            name={attachment.$file.name}
            size={attachment.$file.size}
            onDownload={handleDownload}
            onRemove={() => {
              onFileChange?.(attachmentType, null);
            }}
          />
        )}
      </div>
      <div>
        {showPrepareForSigningIcon ? (
          <Button
            variant="ghost"
            size="icon"
            tooltip={
              canOpenSignForm
                ? hasSigningTemplate
                  ? "Edit Signing Prep"
                  : "Prepare for Signing"
                : "Save document before opening signing"
            }
            className={cn(
              "h-8 w-8 p-0 flex justify-center items-center text-foreground opacity-70 " +
                "hover:bg-transparent hover:text-foreground hover:opacity-100 focus-visible:ring-0 focus-visible:ring-offset-0",
              !canOpenSignForm && "opacity-30"
            )}
            disabled={!canOpenSignForm}
            onClick={() => {
              if (!documentId) return;

              navigate(
                new AppUrlPath(tenantId).getDocumentSignPath(`${documentId}`)
              );
            }}
          >
            <FilePenLine
              size={26}
              className="relative -top-[1px] text-current"
            />
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export interface DocumentAttachmentGridProps {
  className?: string;
  children?: React.ReactNode;
}
export const DocumentAttachmentGrid = ({
  className,
  children,
}: DocumentAttachmentGridProps) => {
  return (
    <div
      className={cn(
        "grid grid-cols-[50px_1fr_50px_3fr_40px] gap-3 items-center",
        className
      )}
    >
      {children}
    </div>
  );
};

export interface DocumentFormProps {
  documentFormData?: DocumentDto;
  partialDocumentFormData?: Partial<DocumentDto>;
  initialUpload?: File;
  initialUrl?: string;
  tenantId: string;
  handleCancel: () => void;
  handleComplete: () => void;
  onSubmit?: (document: DocumentDto) => void;
  addAnother?: () => void;
  addToItem?: (document: DocumentDto) => void;
  viewOnly?: boolean;
  handleGoBack?: () => void;
  cancelButtonLabel?: string;
  withinDialog?: boolean;
  withSaveAndSignButton?: boolean;
}
export const DocumentForm = ({
  documentFormData,
  partialDocumentFormData,
  initialUpload,
  initialUrl,
  tenantId,
  handleCancel,
  handleComplete,
  onSubmit,
  addAnother,
  addToItem,
  viewOnly,
  handleGoBack,
  cancelButtonLabel,
  withinDialog,
  withSaveAndSignButton,
}: DocumentFormProps) => {
  useBlockUnload(!viewOnly);
  const navigate = useNavigate();
  const [
    confirmOutdatedSigningTemplateOpen,
    setConfirmOutdatedSigningTemplateOpen,
  ] = useState(false);

  const tenant = useTenantStore((s) => s.tenant);
  const hasMinRole = useUserStore((s) => s.hasMinRole);

  const activeTeamId = useActiveTeamIdOrOrg() ?? "";

  const isEditor =
    documentFormData?.teamId === activeTeamId && hasMinRole("edit");

  const validationSchema = yup.object({
    name: yup.string().required("Document name is required"),
    // description: yup.string().required("Document description is required"),
  });
  const { data: uploadTypes } = useGetDocumentUploadTypes();

  const trackNew = useTrackedNewObjects();

  const formAction = useRef<"add" | "addAnother" | "addToItem" | "saveAndSign">(
    "add"
  );

  const { data: ordinances } = useOrdinanceList(tenantId, "org");
  const ordinanceCategories = useMemo(
    () =>
      [
        ...new Set(
          ordinances
            ?.map((document) => document.ordinanceData?.category)
            .filter((category) => category != null) ?? []
        ),
      ].sort(),
    [ordinances]
  );

  const { data: meetings } = useMeetingList(tenantId, useActiveTeamId() || "");
  const minutesSelectorMeeting = useMemo(() => {
    const tomorrow = addDays(new Date(), 1);
    return meetings
      ?.slice()
      .sort((a, b) =>
        a.startTime > b.startTime ? -1 : a.startTime < b.startTime ? 1 : 0
      )
      .filter((meeting) => meeting.startTime < tomorrow);
  }, [meetings]);
  const agendaSelectorMeeting = useMemo(
    () =>
      meetings
        ?.slice()
        .sort((a, b) =>
          a.startTime > b.startTime ? -1 : a.startTime < b.startTime ? 1 : 0
        ),
    [meetings]
  );

  const defaults = useContext(DocumentDefaultsContext);

  // build the initial values for the document form
  const initialValues: AugmentedDocument = useMemo(() => {
    const doc = documentRepo.init(
      documentFormData || {
        ...defaults,
        teamId: activeTeamId,
        ...partialDocumentFormData,
      }
    ) as AugmentedDocument;

    if (!documentFormData && (initialUrl || initialUpload)) {
      const attach: AugmentedAttachment = {
        ...attachmentRepo.init(),
        $file: null,
        $url: "",
      };

      if (initialUpload) {
        attach.$file = initialUpload;
      } else {
        attach.$url = initialUrl ?? "";
      }
      doc.attachments.push(attach);
      doc.linkedAttachment = attach.attachmentId;
    }

    doc.attachments = prepareAugmentedAttachments(
      doc.attachments as Attachment[]
    );

    return doc;
  }, [
    documentFormData,
    partialDocumentFormData,
    activeTeamId,
    defaults,
    initialUpload,
    initialUrl,
  ]);

  const {
    invalidNumber,
    markReservedNumberCommitted,
    queueOperation: queueLegislationNumberOperation,
    releaseReservedNumber,
    rememberValidNumber,
    reservedNumberRef,
    setInvalidNumber,
    validateNumber,
  } = useLegislationNumberReservation({
    initialValues,
    tenant,
    tenantId,
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        if (!tenantId) {
          console.log("No tenant ID", tenantId);
          return;
        }

        const doc: Document = { ...values, attachments: [] };

        let documentId = values.id;

        let updatedDocument: DocumentDto;
        if (!documentFormData) {
          updatedDocument = await documentRepo.create(tenantId, doc);
          documentId = updatedDocument.id;
        }

        // process attachments:
        // - upload $file for new file attachments
        // - copy $url to url for url attachments
        const preAttachments = values.attachments;
        const attachments: Attachment[] = [];

        for (const { $file, $url, ...attachData } of preAttachments) {
          let attach: Attachment;

          if (attachData.isUrl) {
            // if url is blank, delete attachment
            if (!$url) continue;

            attach = {
              attachmentType: attachData.attachmentType,
              isUrl: true,
              name: attachData.name || "URL",
              mimeType: "",
              url: $url,
              path: "",
              attachmentId: attachData.attachmentId,
            };
          } else {
            // if no file, delete attachment
            if (!$file) continue;

            if ($file instanceof File) {
              // upload new $file
              attach = await attachmentRepo.upload({
                file: $file,
                tenantId,
                documentId,
                attachmentId: attachData.attachmentId,
                attachmentType: attachData.attachmentType,
              });
            } else {
              // copy plain object $file fields
              attach = {
                ...attachData,
                name: $file.name,
                url: $file.url,
                path: $file.path,
                sizeBytes: $file.size,
              };
            }
          }

          attachments.push(attach);
        }

        // generate and save new document
        doc.attachments = attachments;

        updatedDocument = await documentRepo.update(tenantId, documentId, doc);

        if (!documentFormData) trackNew("document", updatedDocument); // track created documents

        if (updatedDocument) onSubmit?.(updatedDocument);
        if (updatedDocument) {
          markReservedNumberCommitted();
        }
        if (!documentFormData) resetForm();
        handleComplete();
        if (formAction.current === "saveAndSign" && updatedDocument) {
          const openSigningPrep = () => {
            navigate(
              new AppUrlPath(tenantId).getDocumentSignPath(updatedDocument.id)
            );
          };

          if (withinDialog) {
            setTimeout(openSigningPrep, 300); // wait for close animation to complete
          } else {
            openSigningPrep();
          }
        }
        if (formAction.current === "addAnother" && addAnother) {
          setTimeout(addAnother, 300); // wait for close animation to complete so component remounts
        }
        if (
          formAction.current === "addToItem" &&
          addToItem &&
          updatedDocument
        ) {
          setTimeout(() => addToItem(updatedDocument), 300); // wait for close animation to complete
        }
      }}
    >
      {({
        values,
        handleBlur,
        handleChange,
        setFieldValue,
        errors,
        touched,
        isSubmitting,
        submitForm,
      }) => {
        const uploadType = (uploadTypes?.uploadTypes ?? []).find(
          (type) => type.id === values.uploadType
        );

        const hasAttachments = hasRealAttachments(values.attachments);

        const editableAttachment = documentRepo.getAttachmentByType(
          values as DocumentDto,
          "editable"
        ) as AugmentedAttachment;
        const originalAttachment = documentRepo.getAttachmentByType(
          values as DocumentDto,
          "original"
        ) as AugmentedAttachment;
        const signedAttachment = documentRepo.getAttachmentByType(
          values as DocumentDto,
          "signed"
        ) as AugmentedAttachment;

        const canSaveAndPrepare =
          !!tenant?.featureFlags?.signatures &&
          isSignablePdfAttachment(originalAttachment);
        const hasSigningTemplate = !!values.submissionTemplate;
        const originalAttachmentChanged =
          getOriginalAttachmentSignature(initialValues.attachments) !==
          getOriginalAttachmentSignature(values.attachments);
        const shouldConfirmOriginalAttachmentChange =
          hasSigningTemplate && originalAttachmentChanged;

        const attachmentByType: Pick<
          Record<AttachmentType, AugmentedAttachment>,
          "editable" | "original" | "signed"
        > = {
          editable: editableAttachment,
          original: originalAttachment,
          signed: signedAttachment,
        };

        const updateAttachment = (
          type: AttachmentType,
          update: Partial<AugmentedAttachment>
        ) => {
          const attachment = attachmentByType[type];
          if (!attachment) return;

          const isUrl = update.isUrl ?? attachment.isUrl;
          const url =
            (isUrl ? update.$url : update.$file === null ? "" : null) ??
            attachment.url;
          const name = (isUrl ? "URL" : update.$file?.name) ?? attachment.name;
          const sizeBytes =
            (isUrl ? 0 : update.$file?.size) ?? attachment.sizeBytes;

          const newAttachment = {
            ...attachment,
            ...update,
            name,
            url,
            sizeBytes,
          };

          setFieldValue(
            "attachments",
            values.attachments.map((other) =>
              other === attachment ? newAttachment : other
            )
          );

          const linkable = isLinkable(newAttachment);

          if (!values.linkedAttachment && linkable) {
            // new attachment was added, and none was previously linked: link this one
            setFieldValue("linkedAttachment", attachment.attachmentId);
          } else if (
            values.linkedAttachment === attachment.attachmentId &&
            !linkable
          ) {
            // linked attachment was removed: link any other, or unlink if this was the last one
            const otherType =
              type === "original"
                ? "signed"
                : type === "signed"
                  ? "original"
                  : null;
            if (otherType) {
              const otherAttachment = values.attachments.find(
                (other) =>
                  other.attachmentType === otherType && isLinkable(other)
              );
              setFieldValue(
                "linkedAttachment",
                otherAttachment?.attachmentId ?? ""
              );
            }
          }
        };
        const submitWithTemplateWarning = () => {
          if (shouldConfirmOriginalAttachmentChange) {
            setConfirmOutdatedSigningTemplateOpen(true);
            return;
          }

          submitForm();
        };
        const setLegislationNumberField = (
          field: LegislationNumberField,
          value: string
        ) => {
          if (field === "resolutionNumber") {
            setFieldValue("resolutionData", {
              ...values.resolutionData,
              number: value,
            } satisfies ResolutionData);
            return;
          }

          setFieldValue("ordinanceData", {
            ...values.ordinanceData,
            [field === "ordinanceNumber" ? "number" : "draftNumber"]: value,
          } satisfies OrdinanceData);
        };
        const handleLegislationNumberChange = (
          field: LegislationNumberField,
          value: string
        ) => {
          releaseReservedNumber(field, value);
          setLegislationNumberField(field, value);
        };

        const footer = (
          <>
            <Button
              variant="destructive"
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                viewOnly ? handleGoBack?.() : handleCancel();
              }}
            >
              {cancelButtonLabel
                ? cancelButtonLabel
                : viewOnly
                  ? "Go Back"
                  : "Cancel"}
            </Button>
            <div className="flex-1" />
            {!viewOnly && (
              <>
                {addToItem ? (
                  <>
                    <Button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        formAction.current = "addToItem";
                        submitWithTemplateWarning();
                      }}
                    >
                      Save &amp; Add to New Item
                    </Button>
                    <div className="w-3" />
                  </>
                ) : null}
                {addAnother ? (
                  <>
                    <Button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        formAction.current = "addAnother";
                        submitWithTemplateWarning();
                      }}
                    >
                      Save &amp; Add Another
                    </Button>
                    <div className="w-3" />
                  </>
                ) : null}
                {withSaveAndSignButton && tenant?.featureFlags?.signatures && (
                  <>
                    <Button
                      type="button"
                      disabled={isSubmitting || !canSaveAndPrepare}
                      tooltip={
                        !canSaveAndPrepare
                          ? "PDF Must Be Attached"
                          : "Save and prepare for signing"
                      }
                      onClick={() => {
                        formAction.current = "saveAndSign";
                        submitWithTemplateWarning();
                      }}
                    >
                      Save &amp; Prep for Signing
                    </Button>
                    <div className="w-3" />
                  </>
                )}
                <Button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    formAction.current = "add";
                    submitWithTemplateWarning();
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </>
        );

        const form = (
          <div className="flex flex-col gap-y-4">
            {!viewOnly && (
              <TitleComposer
                titleLabel="*Document Title"
                isNew={!documentFormData}
                isSubmitting={isSubmitting}
                value={values.name}
                onChange={function (next: string): void {
                  setFieldValue("name", next);
                }}
              />
            )}
            <Input
              disabled
              label={viewOnly ? "*Document Title" : ""}
              id="name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.name && touched.name}
              errorMessage={errors.name}
            />
            <TextArea
              disabled={viewOnly || isSubmitting}
              label="Document Description"
              id="description"
              name="description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.description && touched.description}
              errorMessage={errors.description}
            />
            <div className="flex flex-row items-center">
              <FormikCheckbox
                disabled={viewOnly || isSubmitting}
                name="visibleOnAgenda"
                label="Visible on Agenda"
                checked={values.visibleOnAgenda ?? true}
                orientation="horizontal"
              />
              <p className="ml-4 mt-2 text-balance">
                If this document is not visible on the agenda, users can still
                download the document by navigating to it in the app.
              </p>
            </div>
            <div className="flex flex-row items-center">
              <FormikCheckbox
                disabled={viewOnly || isSubmitting}
                name="confidential"
                label="Confidential"
                checked={values.confidential}
                orientation="horizontal"
              />
              {values.confidential && (
                <p className="ml-4 mt-2 text-balance">
                  File will only be downloadable by admins, editors, and voting
                  members of teams approving this document.
                </p>
              )}
            </div>
            {values.confidential && (
              <CheckboxGroupInput
                disabled={viewOnly || isSubmitting}
                orientation="vertical"
                label="Confidential due to:"
                options={[
                  {
                    label: "Personnel",
                    value: "personnel",
                  },
                  {
                    label: "Bargaining",
                    value: "bargaining",
                  },
                  {
                    label: "Real Property",
                    value: "realproperty",
                  },
                  {
                    label: "Litigation",
                    value: "litigation",
                  },
                  {
                    label: "Quasi-judicial Deliberation",
                    value: "quasijudicialdeliberation",
                  },
                  {
                    label: "Doesn't Apply to Local Government",
                    value: "doesntapply",
                  },
                  {
                    label: "Confidential for Public Safety",
                    value: "publicsafety",
                  },
                  {
                    label: "Proprietary Information",
                    value: "proprietary",
                  },
                ].map(({ label, value }) => ({ key: value, label }))}
                value={
                  values.confidentialReason
                    ? arrayify(values.confidentialReason)
                    : []
                }
                onValueChange={(value) => {
                  setFieldValue("confidentialReason", value);
                }}
              />
            )}
            <div>
              <SelectInput
                label="Type of Document"
                disabled={viewOnly || hasAttachments || isSubmitting}
                value={values.uploadType}
                placeholder="Select a Document Type"
                onValueChange={(value) => {
                  if (value === values.uploadType) return;

                  // clear attachments
                  setFieldValue("attachments", prepareAugmentedAttachments([]));
                  setFieldValue("linkedAttachment", undefined);

                  setFieldValue("uploadType", value);
                }}
                options={(uploadTypes?.uploadTypes ?? []).map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.label}
                  </SelectItem>
                ))}
              />
              <p className="mt-2">
                {uploadType?.example ? (
                  <>
                    Examples: {uploadType?.example}
                    <br />
                  </>
                ) : null}
                {uploadType?.storage === "long"
                  ? "Long-term storage constraints will apply."
                  : "No upload constraints apply."}
              </p>
            </div>

            {/* Attachments */}
            <Label
              label={
                <>
                  Attachments
                  {!values.uploadType && (
                    <span className="text-sm inline-block ml-4">
                      (Select a document type in order to upload a file.)
                    </span>
                  )}
                </>
              }
              className="py-1"
            />

            <DocumentAttachmentGrid>
              <DocumentAttachmentGridHeader />
              {attachmentTypeList.map((type) => (
                <DocumentAttachmentRow
                  key={type}
                  disabled={isSubmitting || !values.uploadType || viewOnly}
                  viewOnly={viewOnly}
                  tenantId={tenantId}
                  documentId={values.id}
                  downloadable={isEditor || !values.confidential}
                  confidential={values.confidential}
                  attachmentType={type}
                  attachment={attachmentByType[type]}
                  hasSigningTemplate={hasSigningTemplate}
                  linkedAttachment={values.linkedAttachment}
                  onLinkSelect={(type) => {
                    const id = attachmentByType[type]?.attachmentId;
                    if (id) setFieldValue("linkedAttachment", id);
                  }}
                  onIsUrlChange={(type, isUrl) => {
                    updateAttachment(type, { isUrl });
                  }}
                  onUrlChange={(type, url) => {
                    updateAttachment(type, { $url: url });
                  }}
                  onFileChange={(type, file) => {
                    updateAttachment(type, { $file: file });
                  }}
                  uploadType={uploadType}
                />
              ))}
            </DocumentAttachmentGrid>

            {/* Link Auto-change Notice */}
            <p>
              Note: the version that is linked may change automatically
              depending on other processes (e.g., when the signed version of the
              file becomes available).
            </p>

            {/* PHMC Requirements */}
            {!viewOnly && (
              <p className="mt-2 text-red-500">
                Notice: File types limited per PHMC requirements.
                <br />
                <a
                  target="_blank"
                  className="underline"
                  href={fileUploadLimitURLs.counties}
                >
                  Details for Counties (7.2, p. 19)
                </a>
                <br />
                <a
                  target="_blank"
                  className="underline"
                  href={fileUploadLimitURLs.municipalities}
                >
                  Details for Municipalities (7.2, p. 15)
                </a>
                <br />
                <br />
                Also, although TIFF and JPEG images are PHMC-compliant, they may
                not be ADA compliant.
                <br />
                <a
                  target="_blank"
                  className="underline"
                  href={fileUploadLimitURLs.ada}
                >
                  Details for ADA compliance
                </a>
              </p>
            )}

            {/* Per-document type options */}

            {values.uploadType === "ordinances" && (
              <div className="flex flex-col gap-y-4">
                <p className="text-base">Ordinance Details</p>
                <div className="flex flex-row gap-x-4">
                  {values.ordinanceData?.status === "passed" ? (
                    <LegislationNumberInput
                      disabled={viewOnly || isSubmitting}
                      field="ordinanceNumber"
                      id="ordinance-number"
                      label="Ordinance Number"
                      value={values.ordinanceData?.number ?? ""}
                      onChange={handleLegislationNumberChange}
                      onBlur={validateNumber}
                    />
                  ) : (
                    <LegislationNumberInput
                      disabled={viewOnly || isSubmitting}
                      field="draftNumber"
                      id="ordinance-draft-number"
                      label="Draft number"
                      value={values.ordinanceData?.draftNumber ?? ""}
                      onChange={handleLegislationNumberChange}
                      onBlur={validateNumber}
                    />
                  )}

                  <AutocompleteInput
                    disabled={viewOnly || isSubmitting}
                    id="ordinance-category"
                    label="Category"
                    labelStyle="text-sm"
                    containerStyle="flex-grow"
                    value={values.ordinanceData?.category ?? ""}
                    onChange={(value) => {
                      setFieldValue("ordinanceData", {
                        ...values.ordinanceData,
                        category: value,
                      } satisfies OrdinanceData);
                    }}
                    autocompleteOptions={ordinanceCategories}
                  />
                  <SelectInput
                    disabled={viewOnly || isSubmitting}
                    label="Status"
                    labelStyle="text-sm"
                    containerStyle="flex-grow"
                    value={values.ordinanceData?.status ?? "proposed"}
                    onValueChange={(value) => {
                      setFieldValue("ordinanceData", {
                        ...values.ordinanceData,
                        status: value as (typeof ordinanceStatusList)[0],
                      } satisfies OrdinanceData);
                    }}
                    options={ordinanceStatusList.map((status) => (
                      <SelectItem key={status} value={status}>
                        {capitalize(status)}
                      </SelectItem>
                    ))}
                  />
                </div>
              </div>
            )}

            {values.uploadType === "resolutions" && (
              <div className="flex flex-col gap-y-4">
                <p className="text-base">Resolution Details</p>
                <div className="flex flex-row gap-x-4">
                  <LegislationNumberInput
                    disabled={viewOnly || isSubmitting}
                    field="resolutionNumber"
                    id="resolution-number"
                    label="Resolution Number"
                    value={values.resolutionData?.number ?? ""}
                    onChange={handleLegislationNumberChange}
                    onBlur={validateNumber}
                  />
                  <SelectInput
                    disabled={viewOnly || isSubmitting}
                    label="Status"
                    labelStyle="text-sm"
                    containerStyle="flex-grow"
                    value={values.resolutionData?.status ?? "proposed"}
                    onValueChange={(value) => {
                      setFieldValue("resolutionData", {
                        ...values.resolutionData,
                        status: value as (typeof resolutionStatusList)[0],
                      } satisfies ResolutionData);
                    }}
                    options={resolutionStatusList.map((status) => (
                      <SelectItem key={status} value={status}>
                        {capitalize(status)}
                      </SelectItem>
                    ))}
                  />
                </div>
              </div>
            )}

            {values.uploadType === "meeting-minutes" && (
              <>
                <Label label="Meeting Minutes" />

                {values.minutesData?.revision != null && (
                  <p>Revision {values.minutesData.revision}</p>
                )}

                <SelectInput
                  label="Meeting"
                  disabled={viewOnly || isSubmitting}
                  value={values.minutesData?.meetingId ?? ""}
                  options={[
                    <SelectItem key="null" value="null">
                      Not Assigned
                    </SelectItem>,
                    ...(minutesSelectorMeeting?.map((meeting) => (
                      <SelectItem key={meeting.id} value={meeting.id}>
                        {meeting.name} (
                        {formatTZ(
                          meeting.startTime,
                          DATETIME_FORMAT,
                          tenant?.timeZone ?? "America/New_York"
                        )}
                        )
                      </SelectItem>
                    )) ?? []),
                  ]}
                  onValueChange={(value) =>
                    setFieldValue(
                      "minutesData.meetingId",
                      value === "null" ? undefined : value
                    )
                  }
                />

                <CheckboxInput
                  disabled={viewOnly || isSubmitting}
                  label="Officially Approved Meeting Minutes"
                  orientation="horizontal"
                  checked={!!values.minutesData?.official}
                  onCheckedChange={(checked) =>
                    setFieldValue("minutesData.official", checked)
                  }
                />
              </>
            )}

            {values.uploadType === "meeting-agenda" && (
              <>
                <Label label="Meeting Agenda" />

                <SelectInput
                  label="Meeting"
                  disabled={viewOnly || isSubmitting}
                  value={values.agendaData?.meetingId ?? ""}
                  options={[
                    <SelectItem key="null" value="null">
                      Not Assigned
                    </SelectItem>,
                    ...(agendaSelectorMeeting?.map((meeting) => (
                      <SelectItem key={meeting.id} value={meeting.id}>
                        {meeting.name} (
                        {formatTZ(
                          meeting.startTime,
                          DATETIME_FORMAT,
                          tenant?.timeZone ?? "America/New_York"
                        )}
                        )
                      </SelectItem>
                    )) ?? []),
                  ]}
                  onValueChange={(value) => {
                    setFieldValue(
                      "agendaData.meetingId",
                      value === "null" ? undefined : value
                    );
                  }}
                />

                <CheckboxInput
                  disabled={viewOnly || isSubmitting}
                  label="Officially Approved Meeting Agenda"
                  orientation="horizontal"
                  checked={!!values.agendaData?.official}
                  onCheckedChange={(checked) =>
                    setFieldValue("agendaData.official", checked)
                  }
                />
              </>
            )}

            <TagSelection
              disabled={viewOnly || isSubmitting}
              label="Tags"
              value={values.tags ?? []}
              onChange={(newTags) => setFieldValue("tags", newTags)}
              enableAutocomplete={true}
              useStandardizedTags={true}
            />

            {!withinDialog && (
              <div className="flex flex-row justify-between">{footer}</div>
            )}
          </div>
        );

        return (
          <>
            <LegislationNumberReservation
              tenantId={tenantId}
              uploadType={values.uploadType}
              ordinanceData={values.ordinanceData}
              resolutionData={values.resolutionData}
              reservedNumberRef={reservedNumberRef}
              queueOperation={queueLegislationNumberOperation}
              onReservedNumber={rememberValidNumber}
              setFieldValue={setFieldValue}
            />
            <ConfirmDialog
              open={!!invalidNumber}
              setOpen={(open) => {
                if (!open) setInvalidNumber(null);
              }}
              title="Number Format Does Not Match"
              message={
                <p>
                  The entered number does not match the selected numbering
                  format (
                  {invalidNumber
                    ? getLegislationNumberFormatLabel(invalidNumber.format)
                    : ""}
                  ). We will not be able to process it automatically.
                </p>
              }
              confirmLabel="Keep Number"
              confirmVariant="secondary"
              cancelLabel="Revert"
              onConfirm={() => {
                setInvalidNumber(null);
              }}
              onCancel={() => {
                if (!invalidNumber) return;

                setLegislationNumberField(
                  invalidNumber.field,
                  invalidNumber.previousValue
                );
                rememberValidNumber(
                  invalidNumber.field,
                  invalidNumber.previousValue
                );
                setInvalidNumber(null);
              }}
            />
            <ConfirmDialog
              open={confirmOutdatedSigningTemplateOpen}
              setOpen={setConfirmOutdatedSigningTemplateOpen}
              title="Signing Template May Need Updates"
              message={
                <>
                  <p>
                    The original attachment was changed or removed, but this
                    document already has a signing template.
                  </p>
                  <p className="mt-2">
                    After saving, open signing prep to review and update the
                    template if needed before sending sign requests.
                  </p>
                </>
              }
              confirmLabel="Save Anyway"
              confirmVariant="secondary"
              onConfirm={() => {
                submitForm();
              }}
            />
            <form
              onSubmit={(evt) => {
                evt.preventDefault();
                formAction.current = "add";
                submitWithTemplateWarning();
              }}
            >
              {withinDialog ? (
                <>
                  <DialogBody>{form}</DialogBody>
                  <DialogFooter className="justify-between">
                    {footer}
                  </DialogFooter>
                </>
              ) : (
                form
              )}
            </form>
          </>
        );
      }}
    </Formik>
  );
};
